import React, { useState, useEffect } from 'react';
import Spinner from '../components/ui/Spinner';
import {
  PlusIcon,
  TruckIcon,
  UsersIcon,
  BanknotesIcon,
  CheckBadgeIcon,
  ExclamationCircleIcon,
  PaperClipIcon,
} from '@heroicons/react/24/solid';
import { useNavigate } from 'react-router-dom';
import DriverHeader from '../components/common/Driver';
import DriverForm from '../components/form/Driver';
import TripHeader from '../components/common/Trip';
import TripForm from '../components/form/Trip';
import VehicleHeader from '../components/common/Vehicle';
import VehicleForm from '../components/form/Vehicle';
import { dashboardData } from '../services/core';
import { getBranches } from '../services/branches';
import { toast } from 'react-toastify';
import { formatDate, formatMD, formatDMY } from '../utils/datetime';
import { USER_PROFILE, USER_ROLE } from '../services/auth';
import { approveDriverExpense, rejectDriverExpense } from '../services/driver_expenses';
import { approveVehicleExpense, rejectVehicleExpense } from '../services/vehicle_expenses';
import { useOverallCount } from '../context/OverallCountContext';
import { useDimensions, useVehicleTypes } from '../context/vehicles';

export default function Dashboard() {
  const today = new Date();
  const role = localStorage.getItem(USER_ROLE);
  const profile = JSON.parse(localStorage.getItem(USER_PROFILE));

  const [trips, setTrips] = useState(0);
  const [driversTotal, setDriversTotal] = useState(0);
  const [driversPresent, setDriversPresent] = useState(0);
  const [vehicles, setVehicles] = useState(0);

  const [showDriverForm, setShowDriverForm] = useState(false);
  const [showTripForm, setShowTripForm] = useState(false);
  const [showVehicleForm, setShowVehicleForm] = useState(false);

  const [expenseTab, setExpenseTab] = useState('driver');
  const [driverExpenses, setDriverExpenses] = useState([]);
  const [driverExpensesCount, setDriverExpensesCount] = useState(0);
  const [vehicleExpenses, setVehicleExpenses] = useState([]);
  const [vehicleExpensesCount, setVehicleExpensesCount] = useState(0);
  const [driverUsed, setDriverUsed] = useState([]);
  const [vehicleUsed, setVehicleUsed] = useState([]);
  const [inUseTab, setInUseTab] = useState('vehicle');
  const [attendanceTab, setAttendanceTab] = useState('present');
  const [attendanceDate, setAttendanceDate] = useState(formatDate(today));
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState(null);
  const [presentDrivers, setPresentDrivers] = useState([]);
  const [presentDriversCount, setPresentDriversCount] = useState(0);
  const [absentDrivers, setAbsentDrivers] = useState([]);
  const [absentDriversCount, setAbsentDriversCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const { refresh: refreshOverallCount } = useOverallCount({ reload: false });
  const { dimensions } = useDimensions({ reload: true });
  const { vehicleTypes } = useVehicleTypes({ reload: true });

  const navigate = useNavigate();

  useEffect(() => {
    if (role === 'admin' && branches.length === 0) {
      fetchBranches();
    } else {
      fetchDashboardData();
    }
  }, []);

  useEffect(() => {
    if (branch) fetchDashboardData();
  }, [branch, attendanceDate]);

  const fetchBranches = async () => {
    await getBranches({ status: 'active', paginate: false })
      .then(([err, data]) => {
        setBranches(data || []);
        setBranch(data?.length > 0 ? data[0].id : null);
      })
      .catch(console.error);
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const month = formatMD(today);
      const date = formatDate(new Date(attendanceDate));
      const branch_id = role !== 'admin' ? profile.branch : branch;
      const [err, data] = await dashboardData({ branch: branch_id, date, month });
      if (err) {
        toast.error('Failed to fetch data.');
      } else {
        setTrips(data.summary.total_trips || 0);
        setDriversTotal(data.summary.total_drivers || 0);
        setDriversPresent(data.summary.present_drivers || 0);
        setVehicles(data.summary.total_vehicles || 0);
        setDriverExpenses(data.pending_driver_expenses || []);
        setDriverExpensesCount(data.summary.driver_expenses_pending || 0);
        setVehicleExpenses(data.pending_vehicle_expenses || []);
        setVehicleExpensesCount(data.summary.vehicle_expenses_pending || 0);
        setDriverUsed(data.ongoing_trips || []);
        setVehicleUsed(data.ongoing_trips || []);
        setPresentDrivers(data.driver_attendance?.present || []);
        setAbsentDrivers(data.driver_attendance?.absent || []);
        setPresentDriversCount(data.summary.present_drivers || 0);
        setAbsentDriversCount(data.summary.absent_drivers || 0);
      }
    } catch (e) {
      toast.error('Failed to fetch data.');
    } finally {
      setLoading(false);
    }
  };

  const onExpenseActionHandled = () => {
    fetchDashboardData();
    refreshOverallCount();
  };

  const handleStatClick = label => {
    if (label === 'trip') navigate('/trips');
    else if (label === 'driver') navigate('/drivers');
    else if (label === 'vehicle') navigate('/vehicles');
  };

  if (loading) return <Spinner />;

  if (showDriverForm) {
    return (
      <>
        <DriverHeader inputSearch="" count={0} driver={null} onBack={() => setShowDriverForm(false)} formName="add_edit" />
        <DriverForm driver={null} onSave={() => { setShowDriverForm(false); fetchDashboardData(); }} onCancel={() => setShowDriverForm(false)} />
      </>
    );
  }

  if (showTripForm) {
    return (
      <>
        <TripHeader search="" count={0} showFilters={false} trip={null} showForm onBack={() => setShowTripForm(false)} />
        <TripForm trip={null} onSave={() => { setShowTripForm(false); fetchDashboardData(); }} onCancel={() => setShowTripForm(false)} />
      </>
    );
  }

  if (showVehicleForm) {
    return (
      <>
        <VehicleHeader dimensions={dimensions} vehicleTypes={vehicleTypes} branches={branches} search="" count={0} onBack={() => setShowVehicleForm(false)} vehicle={null} formName="add_edit" />
        <VehicleForm vehicle={null} onSave={() => { setShowVehicleForm(false); fetchDashboardData(); }} onCancel={() => setShowVehicleForm(false)} />
      </>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-4 md:px-8 space-y-6 bg-gray-50 min-h-screen uppercase">

      {/* ── Pending expenses alert banner ── */}
      {(driverExpensesCount + vehicleExpensesCount) > 0 && (
        <div
          className="flex items-center justify-between gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 shadow-sm cursor-pointer hover:bg-amber-100 transition-colors"
          onClick={() => {
            document.getElementById('expenses-section')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-amber-400 shrink-0">
              <BanknotesIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-amber-800 leading-tight">
                {driverExpensesCount + vehicleExpensesCount} Pending Expense{(driverExpensesCount + vehicleExpensesCount) > 1 ? 's' : ''} Awaiting Action
              </p>
              <p className="text-xs text-amber-600 mt-0.5 normal-case">
                {driverExpensesCount > 0 && `${driverExpensesCount} driver`}
                {driverExpensesCount > 0 && vehicleExpensesCount > 0 && ' · '}
                {vehicleExpensesCount > 0 && `${vehicleExpensesCount} vehicle`}
              </p>
            </div>
          </div>
          <span className="inline-flex items-center justify-center rounded-full bg-amber-400 text-white text-sm font-bold min-w-[32px] h-8 px-2 shrink-0">
            {driverExpensesCount + vehicleExpensesCount}
          </span>
        </div>
      )}

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-6">
        <StatCard
          bg="bg-blue-50" border="border-blue-200"
          icon={<TruckIcon className="w-8 h-8 md:w-12 md:h-12 text-blue-600/50" />}
          value={trips} label="Trips" addColor="bg-blue-600"
          onAdd={() => setShowTripForm(true)}
          onStatClick={() => handleStatClick('trip')}
        />
        <StatCard
          bg="bg-green-50" border="border-green-200"
          icon={<UsersIcon className="w-8 h-8 md:w-12 md:h-12 text-green-500/60" />}
          value={driversTotal} label="Drivers" addColor="bg-green-600"
          extra={
            <div className="flex items-center gap-1 mt-1">
              <CheckBadgeIcon className="w-4 h-4 text-green-500" />
              <span className="text-green-600 text-xs md:text-sm">{driversPresent} Present</span>
            </div>
          }
          onAdd={() => setShowDriverForm(true)}
          onStatClick={() => handleStatClick('driver')}
        />
        <StatCard
          bg="bg-yellow-50" border="border-yellow-200"
          icon={<TruckIcon className="w-8 h-8 md:w-12 md:h-12 text-yellow-400/80" />}
          value={vehicles} label="Vehicles" addColor="bg-yellow-400"
          onAdd={() => setShowVehicleForm(true)}
          onStatClick={() => handleStatClick('vehicle')}
        />
      </div>

      {/* ── Three sections ── */}
      <div className="flex flex-col gap-6">

        {/* Expenses */}
        <Section id="expenses-section">
          <Tabs
            tabs={[
              { id: 'driver', label: 'Driver', count: driverExpensesCount },
              { id: 'vehicle', label: 'Vehicle', count: vehicleExpensesCount },
            ]}
            active={expenseTab}
            onChange={setExpenseTab}
            title="Expenses"
            count={driverExpensesCount + vehicleExpensesCount}
            icon={<BanknotesIcon className="w-5 h-5 text-green-500" />}
          />
          <ExpensesSection
            entity={expenseTab}
            expensesData={expenseTab === 'driver' ? driverExpenses : vehicleExpenses}
            onActionHandled={onExpenseActionHandled}
          />
        </Section>

        {/* In Use */}
        <Section>
          <Tabs
            tabs={[
              { id: 'vehicle', label: 'Vehicle' },
              { id: 'driver', label: 'Driver' },
            ]}
            active={inUseTab}
            onChange={setInUseTab}
            title={inUseTab === 'vehicle' ? 'Vehicles In Use' : 'Drivers In Use'}
            icon={inUseTab === 'vehicle'
              ? <TruckIcon className="w-5 h-5 text-blue-600" />
              : <UsersIcon className="w-5 h-5 text-green-600" />}
          />
          {inUseTab === 'vehicle'
            ? <InUseTable type="vehicle" items={vehicleUsed} />
            : <InUseTable type="driver" items={driverUsed} />}
        </Section>

        {/* Attendance */}
        <Section>
          <Tabs
            tabs={[
              { id: 'present', label: 'Present', count: presentDriversCount },
              { id: 'absent', label: 'Absent', count: absentDriversCount },
            ]}
            active={attendanceTab}
            onChange={setAttendanceTab}
            title="Attendance"
            count={presentDriversCount + absentDriversCount}
          />
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              type="date"
              className="border px-2 py-1 text-sm rounded uppercase"
              value={attendanceDate}
              onChange={e => setAttendanceDate(e.target.value)}
            />
            {role === 'admin' && (
              <select
                className="border px-2 py-1 text-sm rounded uppercase"
                value={branch}
                onChange={e => setBranch(e.target.value)}
              >
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
          </div>
          {attendanceTab === 'present'
            ? <AttendanceList drivers={presentDrivers} color="bg-green-50" icon={<CheckBadgeIcon className="w-4 h-4 text-green-600 mr-1" />} />
            : <AttendanceList drivers={absentDrivers} color="bg-red-50" icon={<ExclamationCircleIcon className="w-4 h-4 text-red-600 mr-1" />} absent />}
        </Section>

      </div>
    </div>
  );
}

/* ─────────────────── Sub-components ─────────────────── */

function Section({ id, children }) {
  return (
    <div id={id} className="bg-white rounded-xl shadow p-4 md:p-6">
      {children}
    </div>
  );
}

function StatCard({ bg, border, icon, value, label, addColor, extra, onAdd, onStatClick }) {
  return (
    <div className={`relative flex items-center rounded-xl shadow px-4 py-4 md:px-6 md:py-6 ${bg} ${border} border`}>
      <div className="absolute top-2 right-2 md:top-3 md:right-3">
        <button className={`rounded-full p-1.5 md:p-2 text-white shadow ${addColor}`} onClick={onAdd} type="button">
          <PlusIcon className="w-4 h-4 md:w-5 md:h-5" />
        </button>
      </div>
      <div className="mr-3 md:mr-6">{icon}</div>
      <div>
        <div
          className="text-2xl md:text-3xl font-bold mb-0.5 cursor-pointer hover:text-blue-700 transition-colors"
          onClick={onStatClick}
        >
          {value}
        </div>
        <div
          className="text-sm md:text-base font-semibold uppercase tracking-wide cursor-pointer hover:text-blue-700 transition-colors"
          onClick={onStatClick}
        >
          {label}
        </div>
        {extra}
      </div>
    </div>
  );
}

function Tabs({ icon, title, count, tabs, active, onChange }) {
  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <span className="uppercase font-semibold text-lg leading-none">{title}</span>
        {!!count && (
          <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 px-2">
            {count}
          </span>
        )}
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {tabs.map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => onChange(tab.id.toLowerCase())}
            className={`px-3 py-1.5 text-sm font-semibold rounded-t-lg transition-all flex items-center gap-1.5 uppercase
              ${active === tab.id.toLowerCase()
                ? 'bg-blue-100 text-blue-700 border-b-2 border-blue-700'
                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
          >
            <span>{tab.label}</span>
            {!!tab.count && (
              <span className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold min-w-[18px] h-4 px-1">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>
    </>
  );
}

function ExpensesSection({ expensesData, entity, onActionHandled }) {
  const onApprove = async (expense) => {
    const fn = entity === 'driver' ? approveDriverExpense : approveVehicleExpense;
    const [err] = await fn(expense.id);
    if (!err) {
      toast.success(`${entity === 'driver' ? 'Driver' : 'Vehicle'} Expense approved.`);
      onActionHandled();
    }
  };

  const onReject = async (expense) => {
    const fn = entity === 'driver' ? rejectDriverExpense : rejectVehicleExpense;
    const [err] = await fn(expense.id);
    if (!err) {
      toast.success(`${entity === 'driver' ? 'Driver' : 'Vehicle'} Expense rejected.`);
      onActionHandled();
    }
  };

  if (expensesData.length === 0) {
    return <div className="text-center text-gray-400 py-8 text-sm">No pending expenses</div>;
  }

  return (
    <div className="max-h-[400px] overflow-auto">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-xl shadow">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-[#293686] text-white text-left uppercase">
            <tr>
              {entity === 'driver' ? <th className="px-4 py-2">Driver</th> : <th className="px-4 py-2">Vehicle</th>}
              <th className="px-4 py-2">Branch</th>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Amount</th>
              <th className="px-4 py-2">Requested By</th>
              <th className="px-4 py-2">Attachment</th>
              <th className="px-4 py-2">Status</th>
              {expensesData.some(e => e.can_approve) && <th className="px-4 py-2">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {expensesData.map(expense => (
              <tr key={expense.id}>
                <td className="px-4 py-2 capitalize">
                  {entity === 'driver' ? expense.driver?.name : expense.vehicle?.vehicle_number}
                </td>
                <td className="px-4 py-2 uppercase font-semibold">
                  {entity === 'driver' ? expense.driver?.branch?.name : expense.vehicle?.branch?.name}
                </td>
                <td className="px-4 py-2">{formatDMY(expense.date)}</td>
                <td className="px-4 py-2 capitalize">{expense.expense_type}</td>
                <td className="px-4 py-2 font-semibold text-[#293686]">₹{expense.amount}</td>
                <td className="px-4 py-2 capitalize">
                  <div className="flex flex-col">
                    <span className="font-semibold">{expense.created_by?.name}</span>
                    <span className="text-xs text-gray-500">{expense.updated_by?.name}</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  {expense.supporting_doc
                    ? <PaperClipIcon className="w-5 h-5 text-blue-600 inline-block" />
                    : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-4 py-2 text-center">
                  <StatusBadge status={expense.status} />
                </td>
                {expense.can_approve && (
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button className="px-2 py-1 text-xs text-green-600 border border-green-600 rounded hover:bg-green-50" onClick={() => onApprove(expense)}>Approve</button>
                      <button className="px-2 py-1 text-xs text-red-600 border border-red-600 rounded hover:bg-red-50" onClick={() => onReject(expense)}>Reject</button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {expensesData.map(expense => (
          <div key={expense.id} className="border rounded-lg p-3 bg-white shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold text-sm capitalize">
                  {entity === 'driver' ? expense.driver?.name : expense.vehicle?.vehicle_number}
                </p>
                <p className="text-xs text-gray-500 uppercase">
                  {entity === 'driver' ? expense.driver?.branch?.name : expense.vehicle?.branch?.name}
                </p>
              </div>
              <StatusBadge status={expense.status} />
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-600 mb-2">
              <div><span className="text-gray-400">Date: </span>{formatDMY(expense.date)}</div>
              <div><span className="text-gray-400">Type: </span><span className="capitalize">{expense.expense_type}</span></div>
              <div><span className="text-gray-400">Amount: </span><span className="font-semibold text-[#293686]">₹{expense.amount}</span></div>
              <div><span className="text-gray-400">By: </span><span className="capitalize">{expense.updated_by?.name || expense.created_by?.name}</span></div>
              {expense.supporting_doc && (
                <div className="flex items-center gap-1">
                  <PaperClipIcon className="w-3.5 h-3.5 text-blue-600" />
                  <span>Attachment</span>
                </div>
              )}
            </div>
            {expense.can_approve && (
              <div className="flex gap-2 mt-2 pt-2 border-t">
                <button className="flex-1 py-1.5 text-xs text-green-600 border border-green-600 rounded hover:bg-green-50" onClick={() => onApprove(expense)}>Approve</button>
                <button className="flex-1 py-1.5 text-xs text-red-600 border border-red-600 rounded hover:bg-red-50" onClick={() => onReject(expense)}>Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function InUseTable({ type, items }) {
  if (items.length === 0) {
    return <p className="text-center text-gray-400 py-6 text-sm">No data</p>;
  }

  return (
    <>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full table-auto text-sm border">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2">Code</th>
              {type === 'vehicle' && <th className="px-3 py-2">Number</th>}
              {type === 'driver' && <><th className="px-3 py-2">Name</th><th className="px-3 py-2">Contact</th></>}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} className="border-t text-center">
                <td className="px-3 py-2">{item.trip_code}</td>
                {type === 'vehicle' && <td className="px-3 py-2">{item.vehicle_number}</td>}
                {type === 'driver' && <><td className="px-3 py-2">{item.driver_name}</td><td className="px-3 py-2">{item.driver_contact}</td></>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {items.map((item, idx) => (
          <div key={idx} className="border rounded-lg p-3 bg-gray-50 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400 text-xs">Trip Code</span>
              <span className="font-semibold">{item.trip_code}</span>
            </div>
            {type === 'vehicle' && (
              <div className="flex justify-between mt-1">
                <span className="text-gray-400 text-xs">Vehicle</span>
                <span>{item.vehicle_number}</span>
              </div>
            )}
            {type === 'driver' && (
              <>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-400 text-xs">Driver</span>
                  <span>{item.driver_name}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-gray-400 text-xs">Contact</span>
                  <span>{item.driver_contact}</span>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

function AttendanceList({ drivers, color, icon, absent }) {
  if (drivers.length === 0) {
    return (
      <div className={`rounded-lg ${color} p-4 text-center text-sm text-gray-400`}>
        No drivers found.
      </div>
    );
  }

  return (
    <div className={`rounded-lg ${color} p-3 md:p-4`}>
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full table-auto text-sm border">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2">Driver Code</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Contact</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map(d => (
              <tr key={d.id} className="border-t text-center">
                <td className="px-3 py-2">{d.driver_code}</td>
                <td className="px-3 py-2 font-semibold">{d.name}</td>
                <td className="px-3 py-2">{d.primary_phone}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-2">
        {drivers.map(d => (
          <div key={d.id} className="bg-white rounded-lg px-3 py-2 shadow-sm flex items-center gap-3">
            <div className="shrink-0">{icon}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm truncate">{d.name}</p>
              <p className="text-xs text-gray-500">{d.driver_code} · {d.primary_phone}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    approved: 'bg-green-600',
    pending: 'bg-yellow-500',
    rejected: 'bg-red-600',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-white text-xs font-semibold capitalize ${colors[status] || 'bg-gray-400'}`}>
      {status}
    </span>
  );
}
